import { Injectable, OnDestroy } from '@angular/core';

import { combineLatest, Observable, ReplaySubject } from 'rxjs';
import { filter, finalize, map, take } from 'rxjs/operators';

import * as _ from 'lodash';

import {
    Channel,
    ChannelType,
    IdentifiedUser,
    IntegrationRequest,
    Motive,
    ReleaseType,
    ScheduleAppointmentConfiguration,
    SessionUser,
    StreamEvent,
    Tag,
    Ticket,
    TicketMode,
    TicketState,
    User,
} from '../models';

import { TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from './authentication.service';
import { ChannelsService } from './channels.service';
import { ConfigurationService } from './configuration.service';
import { CountersService } from './counters.service';
import { AngularFirestoreCollection, AngularFirestoreQuery, FirestoreService, FirestoreTimestamp } from './firestore';
import { MotivesService } from './motives.service';
import { ConversationControlReleasedByOperatorEventStreamEvent, StreamEventDTO, StreamEventsService } from './stream-events.service';
import { TagsService } from './tags.service';
import { UsersService } from './users.service';
import { UUIDService } from './uuid.service';
import { VoiceService } from './voice.service';

import { OutboundCallError } from '../errors';
import { WebSocketConnection, WebSocketConnectionService, WebSocketConnectionStatus } from './web-socket';

export interface TicketDTO {
    id: string;
    index: string;
    channelId: string;
    state: TicketState;
    mode: TicketMode;
    createdAt: FirestoreTimestamp;
    updatedAt: number;
    escalatedAt: FirestoreTimestamp;
    controlTakenAt: FirestoreTimestamp;
    preAssignedAt: FirestoreTimestamp;

    tagsIds: string[];
    operatorsTagsIds: string[];

    motiveId: string;

    customer: {
        id: string;
        name: string;
    };

    operatorId: string;
    streamId: string;
    firstStreamEventId: string;
    lastStreamEventId: string;
    latestStreamEvent: StreamEvent;
    latestChatEvent: StreamEvent;
    lastReadStreamEventId: string;

    controlHistory: {
        operatorId: string;
        takenOn: FirestoreTimestamp;
        releasedOn: FirestoreTimestamp;
        releaseType: ReleaseType;
    }[];

    counters?: {
        totalCustomerMessages?: number;
        unreadMessages?: {
            [key: string]: number;
        };
    };

    lastCallDuration: number;
    chatSessionId: string;

    isAnswered?: boolean;
    failedOutboundAttempts?: number;
    lastCallEndedAt?: FirestoreTimestamp;
    lastCallStartedAt?: FirestoreTimestamp;
    lastOutboundAttemptAt?: FirestoreTimestamp;
    // TODO: When customers and users
    // customerId: string;
    // operatorId: string;

    identifiedUsers?: IdentifiedUser[];
    clientConnectionId: string;

    integrationRequests?: IntegrationRequest[];
}

export interface TicketsFilter {
    dateRange?: {
        end: Date;
        start: Date;
    };

    endAtDate?: Date;

    endBeforeDate?: Date;

    limit?: number;

    mode?: TicketMode;

    operatorId?: string;

    orderBy?: {
        property: string;
        direction: 'asc' | 'desc';
    };

    states?: TicketState[];

    startAfterDate?: Date;

    startAtDate?: Date;

    tags?: Tag[];

    users?: User[];

    channelId?: string;

    searchTerm?: string;
}

@Injectable({
    providedIn: 'root',
})
export class TicketsService implements OnDestroy {
    private static TICKETS_UNASSIGNED: string = 'TICKETS_UNASSIGNED';
    private static UNASSIGNED_WAITING_TIME: string = 'UNASSIGNED_WAITING_TIME';
    private static SETTINGS_TAGS_PRIORITIES: string = 'tags-priorities';

    private tags: Tag[];
    private channels: Channel[];
    private ratio: number; // ratio of waiting time
    private averageTime: number; // average of waiting time in seconds
    private mustSort: boolean; // indicates when to sort the unassigned tickets by priorities

    private pendingTickets: TicketDTO[];
    private pendingTicketsSubject: ReplaySubject<TicketDTO[]>;
    private webSocketConnectionSubject: ReplaySubject<WebSocketConnectionStatus>;
    private webSocketConnection: WebSocketConnection;

    private get collection(): AngularFirestoreCollection<TicketDTO> {
        return this.firestoreService.tenantDependentCollection<TicketDTO>(this.authenticationService.user.tenantId, 'tickets');
    }

    private scheduleAppointmentConfigurationSubject: ReplaySubject<ScheduleAppointmentConfiguration>;

    constructor(
        private authenticationService: AuthenticationService,
        private firestoreService: FirestoreService,
        private tagsService: TagsService,
        private channelsService: ChannelsService,
        private usersService: UsersService,
        private streamEventsService: StreamEventsService,
        private webSocketService: WebSocketConnectionService,
        private configurationService: ConfigurationService,
        private uuid: UUIDService,
        private countersService: CountersService,
        private voiceService: VoiceService,
        private motivesService: MotivesService,
        private translate: TranslateService
    ) {
        this.scheduleAppointmentConfigurationSubject = new ReplaySubject<ScheduleAppointmentConfiguration>();

        this.configurationService.getTenantConfiguration('scheduleAppointment').subscribe((res) => {
            this.scheduleAppointmentConfigurationSubject.next({
                enabled: res.enabled,
                visible: res.visible,
            });
        });

        this.configurationService.getTenantConfiguration('ticketsSocketEnabled').subscribe((res) => {
            if (res && res.enabled) {
                this.webSocketConnectionSubject.next('OFFLINE');
                this.startWebSocketConnectionForPendingTickets();
            } else {
                this.webSocketConnectionSubject.next('DISABLED');
                this.stopWebSocketConnectionForPendingTickets();
            }
        });

        this.pendingTickets = [];
        this.pendingTicketsSubject = new ReplaySubject<TicketDTO[]>();
        this.webSocketConnectionSubject = new ReplaySubject<WebSocketConnectionStatus>();

        this.tagsService.tags().subscribe((res) => (this.tags = res));
        this.channelsService.channels().subscribe((res) => (this.channels = res));

        combineLatest([
            this.configurationService.getConfiguration('tagsPriorities'),
            this.configurationService.getTenantConfiguration('tagsPriorities'),
            this.countersService.counter(TicketsService.TICKETS_UNASSIGNED),
            this.countersService.counter(TicketsService.UNASSIGNED_WAITING_TIME),
            this.configurationService.getConfiguration('settingsEntries'),
            this.configurationService.getTenantConfiguration('settingsEntries'),
        ])
            .pipe(
                map((result) => {
                    const ratio = (result[1] && result[1].ratio) ?? (result[0] && result[0].ratio) ?? 1.5;
                    const averageTime = result[2] !== 0 && result[3] !== 0 ? result[3] / result[2] : 0;
                    const keys = (result[5] && result[5].keys) ?? (result[4] && result[4].keys) ?? [];
                    return [ratio, averageTime, keys];
                })
            )
            .subscribe((result: [number, number, string[]]) => {
                this.ratio = result[0];
                this.averageTime = result[1];
                this.mustSort = result[2].includes(TicketsService.SETTINGS_TAGS_PRIORITIES);
            });

        this.authenticationService.shouldClearUserSession().subscribe(async (clearUserSession: boolean) => {
            if (clearUserSession) {
                await this.releaseTicketsFromOperator(this.authenticationService.user as User, true);
            }
        });
    }

    ngOnDestroy(): void {
        this.stopWebSocketConnectionForPendingTickets();
    }

    getScheduleAppointmentConfiguration(): Observable<ScheduleAppointmentConfiguration> {
        return this.scheduleAppointmentConfigurationSubject.asObservable();
    }

    async startNewTicketForOutboundCall(
        ticketId: string,
        operatorId: string,
        operatorName: string,
        ticketIdentifiedUser: IdentifiedUser
    ): Promise<Ticket> {
        const tenantId = this.authenticationService.user.tenantId;
        const startNewTicketForOutboundCallCallableFunction = this.firestoreService.cloudFunction('startNewTicketForOutboundCall');

        const streamId = ticketIdentifiedUser?.phoneNumber;

        const result = await startNewTicketForOutboundCallCallableFunction({
            tenantId,
            ticketId,
            streamId,
            operatorId,
            operatorName,
            clientData: {
                birthDate: ticketIdentifiedUser?.birthDate,
                email: ticketIdentifiedUser?.email,
                firstName: ticketIdentifiedUser?.firstName,
                fiscalNumber: ticketIdentifiedUser?.fiscalNumber,
                fullName: ticketIdentifiedUser?.fullName,
                gender: ticketIdentifiedUser?.gender,
                internalId: ticketIdentifiedUser?.internalId,
                lastName: ticketIdentifiedUser?.lastName,
                phoneNumber: ticketIdentifiedUser?.phoneNumber,
            },
        });

        if (result && result.data && result.data.code) {
            switch (result.data.code) {
                case 'permission-denied':
                    throw new OutboundCallError(
                        'PERMISSION_DENIED',
                        "The authenticated user doesn't have permissions to perform this action."
                    );
                case 'invalid-argument':
                    throw new OutboundCallError(
                        'MISSING_ARGUMENT',
                        "The property 'tenantId', 'ticketId', 'operatorId' or 'operatorName' is missing."
                    );
                default:
                    throw new OutboundCallError('UNKNOWN', 'An unknown error occurred.');
            }
        }

        return result.data as Ticket;
    }

    private observablePendingTickets(ticketsFilter?: TicketsFilter): Observable<Ticket[]> {
        if (this.webSocketConnection && this.webSocketConnection.hasConnection()) {
            return combineLatest([
                this.pendingTicketsSubject,
                this.channelsService.channels(),
                this.tagsService.tags(),
                this.usersService.users(undefined, true),
                this.motivesService.motives(),
            ]).pipe(
                map((res) => {
                    let tickets = res[0];
                    const channels = res[1];
                    const tags = res[2];
                    const users = res[3];
                    const motives = res[4];
                    const sessionUserId = this.authenticationService.user.id;

                    if (ticketsFilter) {
                        tickets = tickets.filter((t) => this.applyFilters(t, ticketsFilter, this.authenticationService.user));
                    }

                    return tickets.map((ticketDTO) => {
                        return this.createTicket(ticketDTO, tags, channels, users, sessionUserId, motives);
                    });
                })
            );
        } else {
            return this.tickets(ticketsFilter);
        }
    }

    escalatedUnassignedTickets(ticketsFilter?: TicketsFilter): Observable<Ticket[]> {
        return this.observablePendingTickets(ticketsFilter).pipe(
            map((tickets: Ticket[]) => {
                if (!this.mustSort) {
                    return tickets;
                }

                const waitingTime: number = this.ratio * this.averageTime; // total of waiting time in seconds
                const currentTime: number = new Date().getTime() / 1000; // current time in seconds

                const createdAt: Ticket[] = [];
                const priority: Ticket[] = [];

                tickets.forEach((ticket) => {
                    let ticketWaitingTime = ticket.escalatedAt;

                    if (
                        ticket.controlHistory &&
                        ticket.controlHistory.length > 0 &&
                        ticket.controlHistory[ticket.controlHistory.length - 1]?.releasedOn
                    ) {
                        ticketWaitingTime = ticket.controlHistory[ticket.controlHistory.length - 1].releasedOn.getTime() / 1000;
                    }

                    const exceedsTime = this.exceedsWaitingTime(ticketWaitingTime, waitingTime, currentTime);

                    if (exceedsTime) {
                        createdAt.push(ticket);
                    } else {
                        priority.push(ticket);
                    }
                });

                priority.sort((a, b) => this.sortTicketsByPriority(a, b));

                return createdAt.concat(priority);
            })
        );
    }

    private exceedsWaitingTime(ticketWaitingTime: number, waitingTime: number, currentTime: number): boolean {
        return currentTime - ticketWaitingTime > waitingTime;
    }

    private sortTicketsByPriority(ticketA: Ticket, ticketB: Ticket): number {
        return this.ticketPriority(ticketB) - this.ticketPriority(ticketA);
    }

    private ticketPriority(ticket: Ticket): number {
        return ticket.systemTags.reduce((acc, tag) => acc + (tag.priority ?? 0), 0);
    }

    private applyFilters(ticket: TicketDTO, filter: TicketsFilter, user: SessionUser): boolean {
        let ticketMatches: boolean = true;

        if (ticketMatches && filter.mode !== null && filter.mode !== undefined) {
            ticketMatches = ticket.mode === filter.mode;
        }

        if (ticketMatches && filter.states && filter.states.length > 0) {
            ticketMatches = filter.states.indexOf(ticket.state) >= 0;
        }

        if (ticketMatches && filter.operatorId !== null && filter.operatorId !== undefined) {
            ticketMatches = ticket.operatorId === filter.operatorId;
        }

        if (ticketMatches && filter.tags && filter.tags.length > 0) {
            const allTags = [...(ticket.tagsIds ?? []), ...(ticket.operatorsTagsIds ?? [])] ?? [];
            ticketMatches =
                _.intersection(
                    allTags,
                    filter.tags.map((tag: Tag) => tag.id)
                ).length > 0;
        }

        if (ticketMatches && filter.dateRange) {
            ticketMatches = ticket.createdAt.toDate() >= filter.dateRange.start && ticket.createdAt.toDate() <= filter.dateRange.end;
        }

        if (ticketMatches && filter.users && filter.users.length > 0) {
            const userIds = filter.users.map((u) => u.id);
            ticketMatches = userIds.includes(ticket.operatorId);
        }

        return ticketMatches;
    }

    tickets(filter?: TicketsFilter): Observable<Ticket[]> {
        let ticketsObs: Observable<TicketDTO[]>;

        const collection = this.collection;
        let query: AngularFirestoreQuery;
        const queryOrRef = () => (query ? query : collection.ref);

        if (filter) {
            if (filter.dateRange) {
                query = queryOrRef().where('createdAt', '>', FirestoreTimestamp.fromDate(filter.dateRange.start));
                query = queryOrRef().where('createdAt', '<=', FirestoreTimestamp.fromDate(filter.dateRange.end));
            }

            if (filter.endBeforeDate) {
                query = queryOrRef().orderBy('createdAt', 'desc').endBefore(filter.endBeforeDate);
            }

            if (filter.endAtDate) {
                query = queryOrRef().orderBy('createdAt', 'desc').startAt(filter.endAtDate);
            }

            // Since firestore supports only an 'array-contains-any' or 'in' operator per query,
            // we had to provide alternative "best-effort" approaches. This affects querying by state, user and tags
            // So we apply this logic: Priority is (1 - state ; 2 - tags ; 3 - operators)
            // - Concerning state and operators, if there is only one item, just use the '==' operator and there is no trouble
            // - The previous is not an option with tags since the tagsIds object is an array and not a string
            // - If there are multiple states in the query, we cant support tag filtering. if there is more than one operator, we have
            // to filter in memory
            // - If there is only one state, perform tag filtering. If there is more than one operator, same logic applies

            const multipleStates = filter.states && filter.states.length > 1;
            const singleState = filter.states && filter.states.length === 1;
            const hasStates = filter.states && filter.states.length > 0;
            const multipleUsers = filter.users && filter.users.length > 1;
            const singleUser = filter.users && filter.users.length === 1;
            const hasTags = filter.tags && filter.tags.length > 0;
            let filterUserIds = null;

            if (singleState) {
                query = queryOrRef().where('state', '==', filter.states[0]);
            }

            if (multipleStates) {
                query = queryOrRef().where('state', 'in', filter.states);

                if (multipleUsers) {
                    filterUserIds = filter.users && filter.users.map((user: User) => user.id);
                }
            } else {
                if (hasStates) {
                    query = queryOrRef().where('state', '==', filter.states[0]);
                }

                if (hasTags) {
                    query = queryOrRef().where(
                        'tagsIds',
                        'array-contains-any',
                        filter.tags.map((tag: Tag) => tag.id)
                    );

                    if (multipleUsers) {
                        filterUserIds = filter.users && filter.users.map((user: User) => user.id);
                    }
                } else if (multipleUsers) {
                    query = queryOrRef().where(
                        'operatorId',
                        'in',
                        filter.users.map((user: User) => user.id)
                    );
                }
            }

            if (singleUser) {
                query = queryOrRef().where('operatorId', '==', filter.users[0].id);
            }

            if (filter.startAtDate) {
                query = queryOrRef().orderBy('createdAt', 'desc').startAt(filter.startAtDate);
            }

            if (filter.startAfterDate) {
                query = queryOrRef().orderBy('createdAt', 'desc').startAfter(filter.startAfterDate);
            }

            if (filter.mode) {
                query = queryOrRef().where('mode', '==', filter.mode);
            }

            if (filter.operatorId) {
                query = queryOrRef().where('operatorId', '==', filter.operatorId);
            }

            if (filter.channelId) {
                query = queryOrRef().where('channelId', '==', filter.channelId);
            }

            if (filter.searchTerm) {
                query = queryOrRef().where('searchTerm', '==', filter.searchTerm);
            }

            if (filter.limit && filter.limit > 0) {
                query = queryOrRef().limit(filter.limit);
            }

            if (filter.orderBy && filter.orderBy.direction && filter.orderBy.property) {
                query = queryOrRef().orderBy(filter.orderBy.property, filter.orderBy.direction);
            }

            const subject = new ReplaySubject<TicketDTO[]>(1);
            const unsubscribeQueryOnSnapshot = query.onSnapshot({
                next: (res) => {
                    const filteredTickets = filterUserIds
                        ? res.docs.filter((doc) => filterUserIds.includes((doc.data() as TicketDTO).operatorId))
                        : res.docs;
                    const ticketsDTOs = filteredTickets.map((doc) => {
                        const ticketDTO = doc.data() as TicketDTO;
                        ticketDTO.id = doc.id;
                        return ticketDTO;
                    });

                    subject.next(ticketsDTOs);
                },
                error: (err) => console.error('tickets_query.OnSnapshot', err),
            });

            // whats when the observable to know when the query snapshot need to be unsubscribed
            ticketsObs = subject.pipe(
                finalize(() => {
                    unsubscribeQueryOnSnapshot();
                })
            );
        } else {
            ticketsObs = collection.snapshotChanges().pipe(
                map((res) =>
                    res.map((doc) => {
                        const ticketDto = doc.payload.doc.data();
                        ticketDto.id = doc.payload.doc.id;
                        return ticketDto;
                    })
                )
            );
        }

        return combineLatest([
            ticketsObs,
            this.tagsService.tags(),
            this.channelsService.channels(),
            this.usersService.users(undefined, true),
            this.motivesService.motives(),
        ]).pipe(
            map((res) => {
                const ticketsDTOs = res[0];
                const tags = res[1];
                const channels = res[2];
                const users = res[3];
                const motives = res[4];
                const sessionUserId = this.authenticationService.user.id;

                const tickets = ticketsDTOs
                    .filter((ticketDTO: TicketDTO) => this.canViewOnHoldTicket(ticketDTO, filter))
                    .map((ticketDTO: TicketDTO) => {
                        return this.createTicket(ticketDTO, tags, channels, users, sessionUserId, motives);
                    });

                // Perform Garbage collection on voice tickets
                tickets
                    .filter(
                        (ticket) =>
                            ticket.channel &&
                            this.channelsService.validateChannelTypes([ticket.channel.type as ChannelType], ['Phone']) &&
                            (ticket.state === 'closed' || ticket.state === 'preClosed')
                    )
                    .forEach((ticket) => this.voiceService.clearTicket(ticket.id));

                return tickets;
            })
        );
    }

    private canViewOnHoldTicket(ticketDTO: TicketDTO, filter: TicketsFilter): boolean {
        if (!filter.states || filter.states.length === 0) {
            return true;
        }

        if (ticketDTO.state === 'onHold' && this.authenticationService.user.id !== ticketDTO.operatorId) {
            return false;
        }

        return true;
    }

    async ticketsByDate(startAfterDate: Date, endBeforeDate: Date, limit: number): Promise<Ticket[]> {
        const res = await this.collection.ref.orderBy('createdAt', 'desc').startAfter(startAfterDate).limit(limit).get();

        return res.docs
            .map((doc) => {
                const ticketDTO = doc.data() as TicketDTO;
                ticketDTO.id = doc.id;
                return ticketDTO;
            })
            .filter((dto) => {
                const seconds = dto.createdAt && dto.createdAt.seconds ? dto.createdAt.seconds : dto.createdAt;
                const correspondingDate = this.timestampToDate(seconds as number);
                return correspondingDate >= endBeforeDate;
            })
            .map((ticketDTO) => {
                return this.createTicket(ticketDTO, this.tags, this.channels);
            });
    }

    private startWebSocketConnectionForPendingTickets(): void {
        if (!this.webSocketConnection || !this.webSocketConnection.hasConnection()) {
            this.webSocketService
                .connect()
                .then((connection: WebSocketConnection) => {
                    this.webSocketConnection = connection;

                    this.webSocketConnection.setCallback('connect', () => {
                        this.webSocketConnectionSubject.next('CONNECTED');
                    });
                    this.webSocketConnection.setCallback('disconnect', () => {
                        this.webSocketConnectionSubject.next('OFFLINE');
                    });

                    this.webSocketConnection.setCallback('ticketsAdded', (data: string) => {
                        const convertToTimestamp = (value: any) => {
                            if (value && value._seconds) {
                                return new FirestoreTimestamp(value._seconds, value._nanoseconds);
                            }
                            return value;
                        };
                        const tickets: TicketDTO[] = JSON.parse(data, (key: string, value: any) => {
                            if (value && key === 'controlHistory') {
                                return value.map((ch: any) => {
                                    return {
                                        operatorId: ch.operatorId,
                                        takenOn: convertToTimestamp(ch.takenOn),
                                        releasedOn: convertToTimestamp(ch.releasedOn),
                                    };
                                });
                            } else {
                                return convertToTimestamp(value);
                            }
                        });
                        this.pendingTickets = this.pendingTickets.filter((ticket) => tickets.map((t) => t.id).indexOf(ticket.id) === -1);
                        this.pendingTickets.push(...tickets);
                        this.pendingTicketsSubject.next(this.pendingTickets);
                    });

                    this.webSocketConnection.setCallback('ticketsRemoved', (data: string) => {
                        const ticketIds: string[] = JSON.parse(data);
                        this.pendingTickets = this.pendingTickets.filter((ticket) => ticketIds.indexOf(ticket.id) === -1);
                        this.pendingTicketsSubject.next(this.pendingTickets);
                    });
                })
                .catch((error) => console.log(error));
        }
    }

    ticketsWebSocketConnection(): Observable<WebSocketConnectionStatus> {
        return this.webSocketConnectionSubject.asObservable();
    }

    private stopWebSocketConnectionForPendingTickets(): void {
        if (this.webSocketConnection) {
            this.webSocketConnection.disconnect();
        }
    }

    ticket(id: string): Observable<Ticket> {
        return combineLatest([
            this.collection
                .doc(id)
                .snapshotChanges()
                // WARNING 15-01-2021
                // Had to comment this code because CGD widget tickets were not being rendered because this filter
                // prevented some tickets to enter map operator below (within the pipe) and therefore method createTicket was not being invoked
                // and dashboard was not presented with any errors whatsoever. This happens because CGD tickets never have index property on them
                // and we cannot filter other tickets that should have index, but for some reason they do not have...
                // WARNING 15-01-2021
                // the 'index' was replaced by 'createdAt'
                .pipe(
                    filter((d) => {
                        // when a ticket is updated, the snapshotChanges is triggered,
                        // but when it is updated on the dashboard, the "first call" has only the changes and not the full object.
                        // To avoid missing data, only updated that contains the "index" property, that only is manipulated by the backend,
                        // is considered a valid update
                        return d.payload.get('createdAt') !== undefined;
                    })
                ),
            this.tagsService.tags(),
            this.channelsService.channels(),
            this.usersService.users(undefined, true),
            this.motivesService.motives(),
        ]).pipe(
            map((res) => {
                const ticketsDoc = res[0];
                const tags = res[1];
                const channels = res[2];
                const users = res[3];
                const motives = res[4];
                const sessionUserId = this.authenticationService.user.id;

                const ticketDTO = ticketsDoc.payload.data() as TicketDTO;
                ticketDTO.id = ticketsDoc.payload.id;

                return this.createTicket(ticketDTO, tags, channels, users, sessionUserId, motives);
            })
        );
    }

    /**
     * Mark a ticket as read by reset the ticket unreadMessages counter for the current operator.
     */
    async markTicketAsRead(ticketId: string, operatorId: string): Promise<void> {
        const data: any = {
            counters: {
                unreadMessages: {},
            },
        };

        data.counters.unreadMessages[operatorId] = 0;

        await this.collection.doc(ticketId).set(data, {
            merge: true,
        });
    }

    /**
     * Update the duration of the lastest call performed on this ticket
     */
    async updateLastCallDuration(ticketId: string, lastCallDuration: number): Promise<void> {
        const data: any = {
            lastCallDuration,
        };

        await this.collection.doc(ticketId).set(data, {
            merge: true,
        });
    }

    /**
     * update when the last ticket call ended
     */
    async lastCallEndedAt(ticketId: string, lastCallEndedAt: Date): Promise<void> {
        const data: any = {
            lastCallEndedAt: FirestoreTimestamp.fromDate(lastCallEndedAt),
        };

        await this.collection.doc(ticketId).set(data, {
            merge: true,
        });
    }

    async releaseTicketsFromOperator(operator: User, finishLogoutAfterCompletion: boolean = false): Promise<void> {
        const tickets = await this.tickets({
            operatorId: operator.id,
            states: ['active', 'preClosed', 'assignedOutbound'],
            mode: 'manual',
        })
            .pipe(take(1))
            .toPromise();

        await Promise.all(
            tickets.map((ticket: Ticket) => {
                const releaseEvent = new ConversationControlReleasedByOperatorEventStreamEvent(
                    this.uuid.generate(),
                    ticket.streamId,
                    ticket.channel.id,
                    operator.id,
                    operator.name,
                    new Date().getTime() / 1000,
                    ticket.id,
                    ticket.chatSessionId,
                    ticket.preferredLanguages,
                    ticket.clientConnectionId
                );

                const releaseEventDTO: StreamEventDTO<ConversationControlReleasedByOperatorEventStreamEvent> = {
                    channelId: ticket.channel.id,
                    streamId: ticket.streamId,
                    event: releaseEvent,
                };

                return this.streamEventsService.sendStreamEvent(releaseEventDTO);
            })
        );

        if (finishLogoutAfterCompletion) {
            this.authenticationService.finishLogout();
        }
    }

    async updateTicketTags(ticketId: string, operatorTags: string[], totalTags: string[]): Promise<void> {
        await this.collection.doc(ticketId).ref.update({
            operatorsTagsIds: operatorTags,
            tagsIds: totalTags,
        });
    }

    async updateTicketAnswered(ticketId: string, value: boolean): Promise<void> {
        await this.collection.doc(ticketId).ref.update({
            isAnswered: value,
        });
    }

    private timestampToDate(timestamp: number, millisecondsToAdd: number = 0): Date {
        return new Date(timestamp * 1000 + millisecondsToAdd);
    }

    getReservedNumbers(): { [reservedNumber: string]: string } {
        return {
            '00266696687': 'ANONYMOUS',
            Anonymous: 'ANONYMOUS',
            '007378742833': 'RESTRICTED',
            Restricted: 'RESTRICTED',
            '002562533': 'BLOCKED',
            Blocked: 'BLOCKED',
            '008656696': 'UNKNOWN',
            Unknown: 'UNKNOWN',
            '0086282452253': 'UNAVAILABLE',
            Unavailable: 'UNAVAILABLE',
        };
    }

    private normalizeVoiceTicketTitle(customerName: string): string {
        // when it is not a composite customerName
        if (customerName.indexOf(':') === -1 || !customerName) {
            return customerName;
        }

        const number = customerName.split(':')[0];

        // This is a static list at an international level
        const reservedNumbers = this.getReservedNumbers();

        // when it is a composite customerName with reserved number and callSid
        if (Object.keys(reservedNumbers).indexOf(number) !== -1) {
            return this.translate.instant(`APP.TICKETS_LIST.RESERVED_NUMBERS.${reservedNumbers[number]}`);
        }

        // fallback
        return customerName;
    }

    private createTicket(
        ticketDto: TicketDTO,
        tags: Tag[],
        channels: Channel[],
        users?: User[],
        sessionUserId?: string,
        motives?: Motive[]
    ): Ticket {
        // calculate the total unread messages of this ticket for the current session user.
        // If the user has already seen this ticket,
        // only the  value associated to his ID at the "unreadMessages" object should be considered.
        // If the user has NEVER seen this ticket,
        // the "totalCustomerMessages" should be used as the unread messages as he has all the messages to be read.
        const counters = ticketDto.counters;
        let totalUnreadMessages = 0;
        if (counters) {
            totalUnreadMessages =
                counters.unreadMessages && counters.unreadMessages[sessionUserId] !== undefined
                    ? counters.unreadMessages[sessionUserId]
                    : counters.totalCustomerMessages
                    ? counters.totalCustomerMessages
                    : 0;
        }

        if (ticketDto.customer) {
            ticketDto.customer.name = this.normalizeVoiceTicketTitle(ticketDto.customer.name);
        }

        return new Ticket(
            ticketDto.id,
            ticketDto.index,
            channels.find((c) => c.id === ticketDto.channelId),
            ticketDto.state,
            ticketDto.mode,
            ticketDto.createdAt.seconds,
            ticketDto.updatedAt,
            ticketDto.escalatedAt ? ticketDto.escalatedAt.seconds : 0,
            ticketDto.controlTakenAt ? ticketDto.controlTakenAt.seconds : 0,
            ticketDto.preAssignedAt ? ticketDto.preAssignedAt.seconds : 0,
            ticketDto.tagsIds ? tags.filter((t) => ticketDto.tagsIds.indexOf(t.id) >= 0) : [],
            ticketDto.operatorsTagsIds ? tags.filter((t) => ticketDto.operatorsTagsIds.indexOf(t.id) >= 0) : [],
            ticketDto.customer,
            users ? users.find((u) => u.id === ticketDto.operatorId) : undefined,
            ticketDto.streamId,
            ticketDto.firstStreamEventId,
            ticketDto.lastStreamEventId,
            this.streamEventsService.normalizeEventCreatedAt(ticketDto.latestStreamEvent),
            this.streamEventsService.normalizeEventCreatedAt(ticketDto.latestChatEvent),
            ticketDto.controlHistory?.map((c) => {
                return {
                    operator: users ? users.find((u) => u.id === c.operatorId) : undefined,
                    takenOn: c.takenOn.toDate(),
                    releasedOn: c.releasedOn?.toDate(),
                    releaseType: c.releaseType,
                };
            }),
            totalUnreadMessages,
            ticketDto.lastCallDuration,
            ticketDto.chatSessionId,
            ticketDto.isAnswered,
            ticketDto.failedOutboundAttempts,
            ticketDto.lastCallEndedAt?.seconds,
            ticketDto.motiveId ? motives.find((m) => m.id === ticketDto.motiveId) : undefined,
            ticketDto.lastCallStartedAt?.seconds,
            ticketDto.lastOutboundAttemptAt?.seconds,
            ticketDto.identifiedUsers,
            ticketDto.clientConnectionId,
            ticketDto.integrationRequests ?? []
        );
    }
}
